import { useEffect, useRef, useState } from 'react';
import { computeRoute } from 'features/Responder/features/Tours';
import { distanceMeters, toSeconds } from 'features/Responder/features/Schedules/features/ScheduleTelemetry/utils';

export const useGoogleEta = ({
  isActive,
  scheduleType,
  originPosition,
  nextCheckpointCenter,
  destinationCenter,
  googleTravelMode,
}) => {
  const [googleEta, setGoogleEta] = useState({
    next: null,
    destination: null,
    status: 'idle',
    error: null,
    updatedAt: null,
  });
  const etaRequestRef = useRef(0);
  const lastEtaOriginRef = useRef(null);

  useEffect(() => {
    if (!isActive || scheduleType !== 'Tour') {
      if (googleEta.status !== 'idle') {
        setGoogleEta({
          next: null,
          destination: null,
          status: 'idle',
          error: null,
          updatedAt: null,
        });
      }
      return;
    }
    if (!originPosition || !destinationCenter) {
      setGoogleEta((prev) => ({
        ...prev,
        next: null,
        destination: null,
        status: 'idle',
        error: null,
      }));
      return;
    }

    const now = Date.now();
    const lastOrigin = lastEtaOriginRef.current;
    const recentlyUpdated =
      lastOrigin &&
      googleEta.updatedAt &&
      now - googleEta.updatedAt < 45_000 &&
      distanceMeters(lastOrigin, originPosition) < 15;
    if (recentlyUpdated) return;

    const requestId = ++etaRequestRef.current;
    lastEtaOriginRef.current = originPosition;
    setGoogleEta((prev) => ({ ...prev, status: 'loading', error: null }));

    const originStop = { lat: originPosition.lat, lng: originPosition.lng };
    const nextStop = nextCheckpointCenter
      ? { lat: nextCheckpointCenter.lat, lng: nextCheckpointCenter.lng }
      : null;
    const destStop = destinationCenter
      ? { lat: destinationCenter.lat, lng: destinationCenter.lng }
      : null;

    const buildRoute = (stop) =>
      stop
        ? computeRoute({
            stops: [originStop, stop],
            travelMode: googleTravelMode,
          })
        : Promise.resolve(null);

    Promise.all([buildRoute(nextStop), buildRoute(destStop)])
      .then(([nextRoute, destRoute]) => {
        if (etaRequestRef.current !== requestId) return;
        const nextSeconds = nextRoute ? toSeconds(nextRoute.totalDuration) : null;
        const destSeconds = destRoute ? toSeconds(destRoute.totalDuration) : null;
        setGoogleEta({
          next: nextSeconds,
          destination: destSeconds,
          status: 'ready',
          error: null,
          updatedAt: Date.now(),
        });
      })
      .catch((err) => {
        if (etaRequestRef.current !== requestId) return;
        console.error('[ScheduleTelemetry] Google ETA error', err);
        setGoogleEta({
          next: null,
          destination: null,
          status: 'error',
          error: err?.message || String(err),
          updatedAt: Date.now(),
        });
      });
  }, [
    isActive,
    scheduleType,
    originPosition?.lat,
    originPosition?.lng,
    nextCheckpointCenter?.lat,
    nextCheckpointCenter?.lng,
    destinationCenter?.lat,
    destinationCenter?.lng,
    googleTravelMode,
    googleEta.status,
    googleEta.updatedAt,
  ]);

  return googleEta;
};
